import { getEntityType } from '@datorama/akita';
import { Y42FeatureType } from '@y42/json-schema';
import { combineLatest, distinctUntilChanged, filter, map, Observable, pairwise } from 'rxjs';
import { AbstractGitQueryEntity, AbstractGitQueryEntityConfig } from '../git/state/abstract-git-query-entity.service';
import { AbstractFeatureChildUIModel, AbstractFeatureEntityState, AbstractFeatureUIModel } from '../models';
import { getUniqueIdNamePair } from './get-unique-id-name-pair';

export abstract class AbstractFeatureQueryEntity<
  State extends AbstractFeatureEntityState = AbstractFeatureEntityState,
  UIModel extends AbstractFeatureUIModel = AbstractFeatureUIModel,
  ChildUIModel extends AbstractFeatureChildUIModel = AbstractFeatureChildUIModel,
  ListViewModel extends AbstractFeatureUIModel = AbstractFeatureUIModel,
> extends AbstractGitQueryEntity<State, UIModel> {
  constructor(
    protected readonly cfg: AbstractGitQueryEntityConfig<State, UIModel> & {
      featureType: Y42FeatureType;
    },
  ) {
    super(cfg);
  }

  abstract readonly active$: Observable<UIModel | undefined>;
  abstract readonly items$: Observable<UIModel[]>;
  abstract toListViewModel(item: UIModel | ChildUIModel): ListViewModel[];

  readonly listItems$?: Observable<ListViewModel[]>;

  readonly loadingCounter$ = this.select((st) => st.ui.loading || 0);
  readonly loading$ = combineLatest([this.selectLoading(), this.loadingCounter$]).pipe(
    map(([loading, counter]) => loading || counter > 0),
    distinctUntilChanged(),
  );

  readonly loadingFinished$ = this.loading$.pipe(
    pairwise(),
    map(([prevLoading, nextLoading]) => prevLoading && !nextLoading),
    filter(Boolean),
  );

  readonly getEntityByName = ($name: getEntityType<State>['$name']) => {
    const entities = this.getAll();
    const entity = entities.find((e) => e.$name === $name);
    return entity;
  };

  /**
   * Returns an object with unique id and name for the resource
   *
   * The generated ID follows this schema:
   * [Safe_Name]-[8 random alpha-numeric characters]
   *
   * @param name
   * @returns
   */
  getUniqueIdNamePair(name: string) {
    return getUniqueIdNamePair({ query: this, name });
  }
}
